/**
 * PlayCanvasGS3DManager - PlayCanvas 引擎的 3DGS 管理器实现
 *
 * 实现 IGS3DManager 接口，作为协调层委托到:
 * - ModelRegistry: 模型注册/查找/注销
 * - ModelLoader: 加载编排
 * - ModelOperations: 拾取/颜色/标量过滤/选中/中心点
 * - FrameUpdater: 帧更新/LOD 稳定性检测/缓存清理
 */

import type * as THREE from 'three'
import {
  areGSplatClipStatesEqual,
  createGSplatClipState,
  type GSplatClipState,
} from '@/plugins/gsplat/clipping/GSplatClipState'
import type { ColorSpaceConfig } from '@/plugins/gsplat/core/ColorSpaceConstants'
import { disposeGSplatSogsRenderResources } from '@/plugins/gsplat/core/GSplatSogsData'
import { GSplatDirector } from '@/plugins/gsplat/lod/GSplatDirector'
import type {
  GSplatManager,
  GSplatManagerOptions,
  GSplatOrderUploadSchedulerOptions,
} from '@/plugins/gsplat/lod/GSplatManager'
import type { GSplatOctree } from '@/plugins/gsplat/lod/GSplatOctree'
import type { GSplatPlacement } from '@/plugins/gsplat/lod/GSplatPlacement'
import { Layer } from '@/plugins/gsplat/lod/Layer'
import { CameraComponent, LayerComposition } from '@/plugins/gsplat/lod/LayerComposition'
import { FrameUpdater } from '@/plugins/gsplat/managers/FrameUpdater'
import { ModelLoader } from '@/plugins/gsplat/managers/ModelLoader'
import { ModelOperations } from '@/plugins/gsplat/managers/ModelOperations'
import { ModelRegistry } from '@/plugins/gsplat/managers/ModelRegistry'
import type { GS3DModelInput } from '@/plugins/gsplat/managers/types'
import type {
  CentersDisplayMode,
  CentersStyle,
} from '@/plugins/gsplat/materials/CentersPointsMaterial'
import type {
  CentersPickOptions,
  CentersPickResult,
} from '@/plugins/gsplat/spatial/CentersPickHandler'
import type { PickOptions, PickResult } from '@/plugins/gsplat/spatial/GSplatPickManager'
import { GSplatPickManager } from '@/plugins/gsplat/spatial/GSplatPickManager'
import type {
  LODConfig,
  LODEventData,
  LODEventType,
  LODLevelStat,
  LODStats,
} from '@/plugins/gsplat/types/ConfigTypes'
import { DEFAULT_LOD_CONFIG } from '@/plugins/gsplat/types/ConfigTypes'
import {
  cloneGSplatDeviceBudgetDiagnostics,
  resolveGSplatDeviceBudgetConfig,
} from '@/plugins/gsplat/utils/deviceBudget'
import { getLoggerManager } from '@/shared/logging'
import type { ClipVolume } from '@/shared/types/core'
import type {
  BoundingBox,
  GSplatDeviceBudgetDiagnostics,
  GSplatShaderEffectLike,
  GSplatVisualActivitySnapshot,
  GSplatWorkBufferModifier,
  GSplatWorkBufferUpdateMode,
  IGS3DManager,
  LoadOptions,
  LoadResult,
  ScalarFilterOptions,
  ScalarHistogramOptions,
  ScalarHistogramResult,
} from '@/shared/types/gsplat'

const log = getLoggerManager().getLogger('gsplat')

type LodStatBuckets = Map<number, { nodes: number; splats: number; files: number }>

function addLodStat(
  target: LodStatBuckets,
  lod: number,
  splats: number,
  nodes = 0,
  files = 0
): void {
  if (!Number.isFinite(lod) || lod < 0) return
  const bucket = target.get(lod) ?? { nodes: 0, splats: 0, files: 0 }
  bucket.nodes += nodes
  bucket.splats += Math.max(0, Math.floor(splats))
  bucket.files += files
  target.set(lod, bucket)
}

function toLodLevelStats(source: LodStatBuckets): LODLevelStat[] {
  return Array.from(source.entries())
    .sort(([a], [b]) => a - b)
    .map(([lod, bucket]) => ({
      lod,
      nodes: bucket.nodes,
      splats: bucket.splats,
      ...(bucket.files > 0 ? { files: bucket.files } : {}),
    }))
}

/**
 * PlayCanvasGS3DManager - 基于 PlayCanvas LOD 系统的 3DGS 管理器
 *
 * 实现 IGS3DManager 接口，提供统一的多引擎 API。
 */
export class PlayCanvasGS3DManager implements IGS3DManager {
  // ==================== 核心组件 ====================

  private scene: THREE.Scene
  private renderer: THREE.WebGLRenderer
  private director: GSplatDirector | null = null
  private composition: LayerComposition | null = null
  private defaultLayer: Layer | null = null

  // ==================== 子模块 ====================

  private registry: ModelRegistry
  private loader: ModelLoader
  private operations: ModelOperations
  private frameUpdater: FrameUpdater
  private disposed = false
  private clipState: GSplatClipState = createGSplatClipState([])

  // ==================== 配置与事件 ====================

  private lodConfig: LODConfig
  private rawLodConfig: Partial<LODConfig>
  private deviceBudgetDiagnostics: GSplatDeviceBudgetDiagnostics
  private eventListeners: Map<LODEventType, Set<(data: any) => void>> = new Map()

  // ==================== 构造函数 ====================

  constructor(
    scene: THREE.Scene,
    renderer: THREE.WebGLRenderer,
    config?: Partial<LODConfig>,
    options?: { withCredentials?: boolean }
  ) {
    this.scene = scene
    this.renderer = renderer
    this.rawLodConfig = { ...(config ?? {}) }
    const resolvedConfig = resolveGSplatDeviceBudgetConfig(
      DEFAULT_LOD_CONFIG,
      this.rawLodConfig,
      this.renderer
    )
    this.lodConfig = resolvedConfig.lodConfig
    this.deviceBudgetDiagnostics = resolvedConfig.diagnostics

    // 初始化子模块
    this.registry = new ModelRegistry()

    this.loader = new ModelLoader({
      scene,
      renderer,
      registry: this.registry,
      director: null,
      composition: null,
      initLODSystem: () => this.initLODSystem(),
      getLodConfig: () => this.lodConfig,
      syncLodConfig: () => this.applyLodConfigToRuntime(),
      getClipState: () => this.clipState,
      emit: (event, data) => this.emit(event as LODEventType, data),
      trackLodStability: (nodeId) => this.frameUpdater.initStabilityTracker(nodeId),
      removeByNodeId: (nodeId) => this.removeByNodeId(nodeId),
      withCredentials: options?.withCredentials,
    })

    this.frameUpdater = new FrameUpdater({
      renderer,
      registry: this.registry,
      getDirector: () => this.director,
      getComposition: () => this.composition,
      getClipState: () => this.clipState,
      ensureCameraRegistered: (camera) => this.ensureCameraRegistered(camera),
      removeByNodeId: (nodeId) => this.removeByNodeId(nodeId),
    })

    this.operations = new ModelOperations({
      scene,
      renderer,
      registry: this.registry,
      getDirector: () => this.director,
      getLastCamera: () => this.frameUpdater.lastCamera,
      getAllManagers: () => this.getAllManagers(),
      getClipState: () => this.clipState,
    })
  }

  // ==================== 私有方法：初始化 ====================

  /** 延迟初始化 LOD 系统 */
  private initLODSystem(): { director: GSplatDirector; composition: LayerComposition } {
    if (this.director !== null) {
      return { director: this.director, composition: this.composition! }
    }

    this.director = new GSplatDirector(this.renderer, {
      managerOptions: this.createManagerOptionsFromLodConfig(),
    })
    this.director.addEventListener('manager:created', (event) => {
      this.applyLodConfigToManager(event.manager)
    })
    this.composition = new LayerComposition()

    this.defaultLayer = new Layer('default')
    this.defaultLayer.enabled = true
    this.composition.addLayer(this.defaultLayer)

    return { director: this.director, composition: this.composition }
  }

  /** 确保相机已注册 */
  private ensureCameraRegistered(camera: THREE.Camera): void {
    if (!this.composition) return

    if (!this.composition.camerasSet.has(camera)) {
      const allLayerIds = this.composition.layerList.map((layer) => layer.id)
      const cameraComponent = new CameraComponent(camera, allLayerIds)
      this.composition.addCamera(cameraComponent)
    }
  }

  // ==================== IGS3DManager 接口实现：生命周期 ====================

  /** 释放所有模型、子系统与事件监听器。 */
  dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true

    try {
      for (const nodeId of Array.from(this.registry.nodeIdToClientId.keys())) {
        this.removeByNodeId(nodeId)
      }

      if (this.director) {
        this.director.destroy()
        this.director = null
      }

      if (this.composition) {
        this.composition.clearLayers()
        this.composition.clearCameras()
        this.composition = null
      }

      this.defaultLayer = null

      this.registry.clear()
      this.loader.dispose()
      this.disposeGpuPicker()
      this.operations.dispose()
      this.frameUpdater.dispose()
      this.eventListeners.clear()
    } finally {
      try {
        disposeGSplatSogsRenderResources(this.renderer)
      } catch {
        // 共享 pass 诊断资源释放失败不应阻止 Renderer 断链。
      }
      // 公开 GS handle 可能在宿主缓存中继续存活；主动切断其 manager 到 Renderer 的路径。
      this.scene = null as unknown as THREE.Scene
      this.renderer = null as unknown as THREE.WebGLRenderer
      this.loader = null as unknown as ModelLoader
      this.operations = null as unknown as ModelOperations
      this.frameUpdater = null as unknown as FrameUpdater
    }
  }

  // ==================== IGS3DManager 接口实现：模型加载 ====================

  /**
   * 加载一个 3DGS 模型。
   *
   * @param url - 资源 URL，支持 .sog / .splat / .ply / LOD 元信息
   * @param options - 可选加载参数，控制解码、变换等
   * @returns 加载结果，含生成的 nodeId、numSplats、包围盒等元信息
   */
  async load(url: string, options?: LoadOptions): Promise<LoadResult> {
    return this.loader.load(url, options)
  }

  // ==================== IGS3DManager 接口实现：模型管理 ====================

  /**
   * 按模型对象移除已加载资源。
   *
   * @param group - 先前 load 返回的模型引用
   * @returns 成功移除返回 true，未找到对应 nodeId 返回 false
   */
  remove(group: GS3DModelInput): boolean {
    const nodeId = this.registry.extractNodeId(group)
    if (!nodeId) {
      log.warn('[PlayCanvasGS3DManager] remove: 无法从 group 对象中提取 nodeId')
      return false
    }
    return this.removeByNodeId(nodeId)
  }

  /**
   * 按 nodeId 移除模型，含 LOD 组和单文件两种形态。
   *
   * @param nodeId - 模型唯一标识
   * @returns 成功移除返回 true，未找到返回 false
   */
  removeByNodeId(nodeId: string): boolean {
    const clientId = this.registry.resolveClientId(nodeId)
    if (!clientId) return false

    // 尝试移除 LOD 模型
    const lodEntry = this.registry.lodGroups.get(clientId)
    if (lodEntry) {
      this.scene.remove(lodEntry.rootNode)
      if (lodEntry.boxHelper) this.scene.remove(lodEntry.boxHelper)
      if (lodEntry.renderMesh) lodEntry.renderMesh.dispose()
      if (lodEntry.centersPoints) lodEntry.centersPoints.dispose()
      if (this.composition) this.composition.removeLayer(lodEntry.layer)
      lodEntry.octree.destroy()

      this.registry.unregisterLOD(clientId)
      this.frameUpdater.clearTracker(nodeId)
      return true
    }

    // 尝试移除单文件模型
    const singleEntry = this.registry.singleFiles.get(clientId)
    if (singleEntry) {
      if (singleEntry.mesh.parent) singleEntry.mesh.parent.remove(singleEntry.mesh)
      singleEntry.mesh.dispose()

      this.registry.unregisterSingle(clientId)
      return true
    }

    return false
  }

  /**
   * 按资源 URL 移除最后注册的存活实例。
   *
   * @param url - 加载时使用的 URL
   * @returns 成功移除返回 true，未找到返回 false
   */
  removeByUrl(url: string): boolean {
    const clientId = this.registry.getLatestClientIdByUrl(url)
    if (!clientId) return false

    const lodEntry = this.registry.lodGroups.get(clientId)
    if (lodEntry) return this.removeByNodeId(lodEntry.nodeId)

    const singleEntry = this.registry.singleFiles.get(clientId)
    if (singleEntry) return this.removeByNodeId(singleEntry.nodeId)

    return false
  }

  /**
   * 按 nodeId 取模型条目。
   *
   * @param nodeId - 模型唯一标识
   * @returns 注册表中的原始条目，未找到返回 undefined
   */
  getByNodeId(nodeId: string): unknown | undefined {
    return this.registry.getByNodeId(nodeId)
  }

  /**
   * 按资源 URL 取最后注册的存活模型条目。
   *
   * @param url - 加载时使用的 URL
   * @returns 注册表中的原始条目，未找到返回 undefined
   */
  getByUrl(url: string): unknown | undefined {
    return this.registry.getByUrl(url)
  }

  /**
   * 获取所有已注册模型的原始条目。
   *
   * @returns 模型条目数组，顺序与注册顺序一致
   */
  getAll(): unknown[] {
    return this.registry.getAll()
  }

  /**
   * 判断是否存在指定 nodeId 的模型。
   *
   * @param nodeId - 模型唯一标识
   * @returns 存在返回 true
   */
  hasByNodeId(nodeId: string): boolean {
    return this.registry.hasByNodeId(nodeId)
  }

  /**
   * 判断是否已加载指定 URL 的模型。
   *
   * @param url - 加载时使用的 URL
   * @returns 存在返回 true
   */
  hasByUrl(url: string): boolean {
    return this.registry.hasByUrl(url)
  }

  /**
   * 判断指定 URL 是否正在加载中。
   *
   * @param url - 加载时使用的 URL
   * @returns 正在加载返回 true
   */
  isLoading(url: string): boolean {
    return this.registry.isLoading(url)
  }

  /**
   * 获取所有已加载模型的 nodeId 列表。
   *
   * @returns nodeId 数组
   */
  getAllNodeIds(): string[] {
    return this.registry.getAllNodeIds()
  }

  /**
   * 将 nodeId 绑定到已有模型。
   *
   * @param group - 目标模型引用
   * @param nodeId - 新的 nodeId
   * @returns 绑定成功返回 true
   */
  setNodeId(group: GS3DModelInput, nodeId: string): boolean {
    return this.registry.setNodeId(group, nodeId)
  }

  // ==================== 可见性 ====================

  /**
   * 切换指定模型的可见性，并同步更新 LOD 与隐藏缓存。
   *
   * @param nodeId - 模型唯一标识
   * @param visible - true 为显示，false 为隐藏
   * @returns 存在并切换成功返回 true
   */
  setVisible(nodeId: string, visible: boolean): boolean {
    const clientId = this.registry.resolveClientId(nodeId)
    if (!clientId) {
      log.warn(`[PlayCanvasGS3DManager] setVisible: nodeId "${nodeId}" not found`)
      return false
    }

    const lodEntry = this.registry.lodGroups.get(clientId)
    if (lodEntry) {
      lodEntry.rootNode.visible = visible

      if (this.director && this.frameUpdater.lastCamera) {
        const manager = this.director.getManager(this.frameUpdater.lastCamera, lodEntry.layer)
        if (manager) {
          const octreeInstance = manager.octreeInstances.get(lodEntry.placement)
          if (octreeInstance) {
            octreeInstance.visible = visible

            if (visible) {
              manager.layerPlacementsDirty = true
              manager.framesTillFullUpdate = 0
              GSplatPickManager.clearGpuPickerLodVersion(manager)
              this.frameUpdater.initStabilityTracker(nodeId, manager)
            }
          }
        }
      }

      lodEntry.layer.gsplatPlacementsDirty = true

      // 缓存管理
      if (!visible) {
        if (!this.frameUpdater.hiddenModelsCache.includes(nodeId)) {
          this.frameUpdater.hiddenModelsCache.push(nodeId)
        }
        this.frameUpdater.clearTracker(nodeId)
      } else {
        const index = this.frameUpdater.hiddenModelsCache.indexOf(nodeId)
        if (index !== -1) this.frameUpdater.hiddenModelsCache.splice(index, 1)
      }

      return true
    }

    const singleEntry = this.registry.singleFiles.get(clientId)
    if (singleEntry) {
      singleEntry.mesh.visible = visible
      return true
    }

    return false
  }

  /**
   * 获取指定模型的 LOD Environment 期望可见状态。
   *
   * @param nodeId - 模型唯一标识
   * @returns 当前状态；非 LOD 或未配置 Environment 时返回 undefined
   */
  getEnvironmentVisible(nodeId: string): boolean | undefined {
    const entry = this.registry.getLODByNodeId(nodeId)
    if (!entry?.octree.environmentUrl) {
      return undefined
    }
    return entry.placement.environmentVisible
  }

  /**
   * 独立切换指定 LOD 模型的 Environment 可见性。
   *
   * 只同步活动渲染集合，不卸载资源或修改 Environment 引用计数。
   *
   * @param nodeId - 模型唯一标识
   * @param visible - true 为显示，false 为隐藏
   * @returns 设置成功返回 true；非 LOD 或未配置 Environment 时返回 false
   */
  setEnvironmentVisible(nodeId: string, visible: boolean): boolean {
    const entry = this.registry.getLODByNodeId(nodeId)
    if (!entry?.octree.environmentUrl) {
      return false
    }

    if (entry.placement.environmentVisible === visible) {
      return true
    }

    entry.placement.environmentVisible = visible
    entry.layer.gsplatPlacementsDirty = true

    for (const manager of this.getAllManagers()) {
      const instance = manager.octreeInstances.get(entry.placement)
      if (!instance) continue

      if (!instance.syncEnvironmentVisibility()) continue

      manager.layerPlacementsDirty = true
      manager.sortNeeded = true
      manager.framesTillFullUpdate = 0
      GSplatPickManager.clearGpuPickerLodVersion(manager)
      this.frameUpdater.initStabilityTracker(nodeId, manager)
    }

    return true
  }

  // ==================== 包围盒 ====================

  /**
   * 按 nodeId 取包围盒。
   *
   * @param nodeId - 模型唯一标识
   * @returns 世界坐标系下的包围盒；未找到返回 undefined
   */
  getBoundingBoxByNodeId(nodeId: string): BoundingBox | undefined {
    return this.operations.getBoundingBoxByNodeId(nodeId)
  }

  /**
   * 取当前所有可见模型的联合包围盒。
   *
   * @returns 世界坐标系下的联合包围盒，无模型时返回空盒
   */
  getCombinedBoundingBox(): BoundingBox {
    return this.operations.getCombinedBoundingBox()
  }

  /**
   * 切换包围盒辅助可视化。
   *
   * @param visible - true 为显示，false 为隐藏
   * @param nodeIds - 作用于指定模型，省略则作用于全部
   */
  setBoxHelperVisible(visible: boolean, nodeIds?: string[]): void {
    this.operations.setBoxHelperVisible(visible, nodeIds)
  }

  // ==================== 每帧更新 ====================

  /**
   * 每帧更新入口，内部会根据相机推动 LOD 与排序。
   *
   * @param camera - 当前相机，内部会按 THREE.Camera 使用
   * @param delta - 与上一帧的时间差，单位秒
   */
  update(camera: unknown, rendererOrOptions?: unknown): void {
    const logicalFrame = this.resolveLogicalFrame(rendererOrOptions)
    if (logicalFrame) {
      this.frameUpdater.update(camera as THREE.Camera, logicalFrame.id, logicalFrame.source)
    } else {
      this.frameUpdater.update(camera as THREE.Camera)
    }

    const delta = this.resolveShaderEffectDelta(rendererOrOptions)
    if (delta !== null) {
      this.frameUpdater.updateShaderEffects(delta)
    }
  }

  advanceShaderEffects(delta = 0): void {
    this.frameUpdater.updateShaderEffects(delta)
  }

  private resolveShaderEffectDelta(rendererOrOptions: unknown): number | null {
    if (typeof rendererOrOptions === 'number') {
      return Number.isFinite(rendererOrOptions) ? Math.max(0, rendererOrOptions) : 0
    }

    if (rendererOrOptions && typeof rendererOrOptions === 'object') {
      const options = rendererOrOptions as { delta?: unknown; effectDelta?: unknown }
      const rawDelta = typeof options.effectDelta === 'number' ? options.effectDelta : options.delta
      if (typeof rawDelta === 'number') {
        return Number.isFinite(rawDelta) ? Math.max(0, rawDelta) : 0
      }
    }

    return null
  }

  private resolveLogicalFrame(rendererOrOptions: unknown): { id: number; source: object } | null {
    if (!rendererOrOptions || typeof rendererOrOptions !== 'object') {
      return null
    }

    const options = rendererOrOptions as {
      logicalFrameId?: unknown
      logicalFrameSource?: unknown
    }
    if (typeof options.logicalFrameId !== 'number' || !Number.isFinite(options.logicalFrameId)) {
      return null
    }

    return {
      id: Math.floor(options.logicalFrameId),
      source:
        options.logicalFrameSource !== null &&
        (typeof options.logicalFrameSource === 'object' ||
          typeof options.logicalFrameSource === 'function')
          ? (options.logicalFrameSource as object)
          : this,
    }
  }

  // ==================== 配置 ====================

  /**
   * 合并更新 LOD 配置，并同步到现有 octree 的 asset loader。
   *
   * @param config - 局部配置，仅覆盖提供的字段
   */
  setConfig(config: Partial<LODConfig>): void {
    this.mergeRawLodConfig(config)
    this.recomputeLodConfig()
    this.applyLodConfigToRuntime()
  }

  private mergeRawLodConfig(config: Partial<LODConfig>): void {
    for (const [key, value] of Object.entries(config) as [
      keyof LODConfig,
      LODConfig[keyof LODConfig],
    ][]) {
      if (value === undefined) {
        delete this.rawLodConfig[key]
      } else {
        ;(this.rawLodConfig as Record<string, unknown>)[String(key)] = value
      }
    }
  }

  private recomputeLodConfig(): void {
    const resolvedConfig = resolveGSplatDeviceBudgetConfig(
      DEFAULT_LOD_CONFIG,
      this.rawLodConfig,
      this.renderer
    )
    this.lodConfig = resolvedConfig.lodConfig
    this.deviceBudgetDiagnostics = resolvedConfig.diagnostics
  }

  private applyLodConfigToRuntime(): void {
    this.loader.syncConfig()
    this.director?.setManagerOptions(this.createManagerOptionsFromLodConfig())

    for (const entry of this.registry.lodGroups.values()) {
      this.applyLodConfigToPlacement(entry.placement)
      entry.placement.splatBudget = Math.max(
        1,
        Math.min(entry.totalPointCount, this.lodConfig.maxCachePoints)
      )
      entry.layer.gsplatPlacementsDirty = true

      if (entry.octree.assetLoader) {
        entry.octree.assetLoader.maxConcurrentLoads = this.lodConfig.maxConcurrentLoads
      }
    }

    for (const manager of this.getAllManagers()) {
      this.applyLodConfigToManager(manager)
      manager.params.dirty = true
      manager.layerPlacementsDirty = true
      manager.sortNeeded = true
      manager.framesTillFullUpdate = 0
    }
  }

  /**
   * 获取当前 LOD 配置。
   *
   * @returns 配置副本，外部修改不影响内部状态
   */
  getConfig(): LODConfig {
    const config = { ...this.lodConfig }
    if (this.lodConfig.deviceBudget) {
      config.deviceBudget = { ...this.lodConfig.deviceBudget }
    }
    if (this.lodConfig.interactiveSortScheduler) {
      config.interactiveSortScheduler = { ...this.lodConfig.interactiveSortScheduler }
    }
    return config
  }

  /**
   * 获取最近一次设备预算策略诊断。
   *
   * @returns 诊断副本，外部修改不影响内部状态
   */
  getDeviceBudgetDiagnostics(): GSplatDeviceBudgetDiagnostics {
    return cloneGSplatDeviceBudgetDiagnostics(this.deviceBudgetDiagnostics)
  }

  // ==================== 统计 ====================

  /**
   * 采集当前 LOD/加载/缓存统计信息。
   *
   * @returns 聚合统计快照
   */
  getStats(): LODStats {
    let visibleNodes = 0
    let loadedNodes = 0
    let cachedPoints = 0
    let pendingLoads = 0
    let activeLoads = 0
    let requestedLoads = 0
    let failedLoads = 0
    let renderedSplats = 0
    let activeSplats = 0
    let texturePixels = 0
    let pendingLodChanges = 0
    let activePlacements = 0
    let lodPresentationPending = false
    let budgetScaleSum = 0
    let budgetScaleCount = 0
    let globalMaxDistance = 0
    let adjustedBudget = 0
    let paddingEstimate = 0
    let rawOptimalSplats = 0
    let budgetTextureWidth = 0
    let cameraAspect = 0
    const clipLodDiagnostics = {
      classifiedNodes: 0,
      polygonCulledNodes: 0,
      boxCulledNodes: 0,
      mixedCulledNodes: 0,
      intersectingNodes: 0,
      excludedSplats: 0,
    }
    const cachedResources = new Set<unknown>()
    const observedOctrees = new Set<GSplatOctree>()
    const loadedLods: LodStatBuckets = new Map()
    const currentLods: LodStatBuckets = new Map()
    const optimalLods: LodStatBuckets = new Map()

    if (this.director) {
      for (const manager of this.director.getAllManagers()) {
        const managerTextureSize = manager.textureSize
        const sortedWorldState = manager.worldStates.get(manager.sortedVersion)
        texturePixels += managerTextureSize * managerTextureSize
        renderedSplats += manager.visibleSplatCount
        activeSplats += sortedWorldState?.totalActiveSplats ?? 0
        pendingLoads += manager.loadingCount
        lodPresentationPending ||= manager.lodPresentationPending
        const budgetDiagnostics = manager.budgetRuntimeDiagnostics
        budgetScaleSum += budgetDiagnostics.budgetScale
        budgetScaleCount++
        globalMaxDistance = Math.max(globalMaxDistance, budgetDiagnostics.globalMaxDistance)
        adjustedBudget += budgetDiagnostics.adjustedBudget
        paddingEstimate += budgetDiagnostics.paddingEstimate
        rawOptimalSplats += budgetDiagnostics.rawOptimalSplats
        budgetTextureWidth = Math.max(budgetTextureWidth, budgetDiagnostics.budgetTextureWidth)
        cameraAspect = budgetDiagnostics.cameraAspect || cameraAspect
        const managerClipDiagnostics = manager.clipLodDiagnostics
        clipLodDiagnostics.classifiedNodes += managerClipDiagnostics.classifiedNodes
        clipLodDiagnostics.polygonCulledNodes += managerClipDiagnostics.polygonCulledNodes
        clipLodDiagnostics.boxCulledNodes += managerClipDiagnostics.boxCulledNodes
        clipLodDiagnostics.mixedCulledNodes += managerClipDiagnostics.mixedCulledNodes
        clipLodDiagnostics.intersectingNodes += managerClipDiagnostics.intersectingNodes
        clipLodDiagnostics.excludedSplats += managerClipDiagnostics.excludedSplats

        for (const instance of manager.octreeInstances.values()) {
          activeLoads += instance.activePendingLoadCount
          activePlacements += instance.activePlacements.size

          if (!observedOctrees.has(instance.octree)) {
            observedOctrees.add(instance.octree)
            const loaderStatus = instance.octree.assetLoader?.getStatus?.()
            requestedLoads += loaderStatus?.total ?? 0
            failedLoads += loaderStatus?.failed ?? 0
          }

          for (let nodeIndex = 0; nodeIndex < instance.nodeInfos.length; nodeIndex++) {
            const info = instance.nodeInfos[nodeIndex]
            const node = instance.octree.nodes[nodeIndex]
            if (info.currentLod >= 0) {
              visibleNodes++
              addLodStat(currentLods, info.currentLod, node?.lods[info.currentLod]?.count ?? 0, 1)
            }
            const optimalFileIndex =
              info.optimalLod >= 0 ? (node?.lods[info.optimalLod]?.fileIndex ?? -1) : -1
            const optimalResourceFailed =
              optimalFileIndex >= 0 && instance.octree.isFileFailed(optimalFileIndex)
            if (
              info.optimalLod >= 0 &&
              info.currentLod !== info.optimalLod &&
              !optimalResourceFailed
            ) {
              pendingLodChanges++
            }
            if (info.optimalLod >= 0) {
              addLodStat(optimalLods, info.optimalLod, node?.lods[info.optimalLod]?.count ?? 0, 1)
            }
          }

          for (const [fileIndex, resource] of instance.octree.fileResources) {
            if (!cachedResources.has(resource)) {
              cachedResources.add(resource)
              cachedPoints += resource.numSplats
              const lod = instance.octree.files[fileIndex]?.lodLevel ?? -1
              addLodStat(loadedLods, lod, resource.numSplats, 0, 1)
            }
          }

          const environmentResource = instance.octree.environmentResource
          if (environmentResource && !cachedResources.has(environmentResource)) {
            cachedResources.add(environmentResource)
            cachedPoints += environmentResource.numSplats
          }
        }
      }
    }

    loadedNodes = cachedResources.size

    return {
      totalGroups: this.registry.lodGroups.size + this.registry.singleFiles.size,
      visibleNodes,
      loadedNodes,
      cachedPoints,
      pendingLoads,
      activeLoads,
      cachedFiles: loadedNodes,
      requestedLoads,
      failedLoads,
      renderedSplats,
      activeSplats,
      texturePixels,
      pendingLodChanges,
      activePlacements,
      lodPresentationPending,
      loadedLods: toLodLevelStats(loadedLods),
      currentLods: toLodLevelStats(currentLods),
      optimalLods: toLodLevelStats(optimalLods),
      budgetScale: budgetScaleCount > 0 ? budgetScaleSum / budgetScaleCount : undefined,
      globalMaxDistance,
      adjustedBudget,
      paddingEstimate,
      rawOptimalSplats,
      budgetTextureWidth,
      cameraAspect,
      clipLodDiagnostics,
    }
  }

  // ==================== 事件 ====================

  /**
   * 订阅 LOD 生命周期事件。
   *
   * @param event - 事件类型
   * @param callback - 回调，接收事件对应的数据
   */
  on<T extends LODEventType>(event: T, callback: (data: LODEventData[T]) => void): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event)!.add(callback)
  }

  /**
   * 解除事件订阅。
   *
   * @param event - 事件类型
   * @param callback - 先前订阅时传入的同一回调引用
   */
  off<T extends LODEventType>(event: T, callback: (data: LODEventData[T]) => void): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) listeners.delete(callback)
  }

  private emit<T extends LODEventType>(event: T, data: LODEventData[T]): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      for (const callback of listeners) {
        try {
          callback(data)
        } catch (error) {
          log.error(
            `[PlayCanvasGS3DManager] Error in ${event} listener: ` +
              (error instanceof Error ? error.message : String(error))
          )
        }
      }
    }
  }

  // ==================== PlayCanvas 特有方法 ====================

  /**
   * 获取 LOD Director，尚未就绪时返回 null。
   *
   * @returns 当前 Director 实例或 null
   */
  getDirector(): GSplatDirector | null {
    return this.director
  }

  /**
   * 获取 Layer 组合对象。
   *
   * @returns 当前 LayerComposition 实例或 null
   */
  getComposition(): LayerComposition | null {
    return this.composition
  }

  /**
   * 获取与指定相机绑定的 GSplatManager。
   *
   * @param camera - 目标相机
   * @param layer - 可选 layer，省略时使用默认 layer
   * @returns 对应 manager；Director 未就绪返回 null
   */
  getManager(camera: THREE.Camera, layer?: Layer): GSplatManager | null {
    if (!this.director) return null
    return this.director.getManager(camera, layer ?? this.defaultLayer!)
  }

  /**
   * 获取全部已创建的 GSplatManager。
   *
   * @returns manager 数组，未就绪返回空数组
   */
  getAllManagers(): GSplatManager[] {
    if (!this.director) return []
    return this.director.getAllManagers()
  }

  getVisualActivitySnapshot(): GSplatVisualActivitySnapshot {
    const reasons: string[] = []

    if (this.frameUpdater.hasStabilityTrackingWork()) {
      reasons.push('gsplat.lodStability')
    }

    for (const manager of this.getAllManagers()) {
      const activity = manager.getVisualActivitySnapshot()
      if (activity.needsNextFrame) {
        reasons.push(...activity.reasons)
      }
    }

    return {
      needsNextFrame: reasons.length > 0,
      reasons: Array.from(new Set(reasons)),
    }
  }

  private applyLodConfigToManager(manager: GSplatManager): void {
    const params = manager.params
    const config = this.lodConfig
    manager.setOrderUploadSchedulerOptions(this.createOrderUploadSchedulerOptions())

    if (typeof config.maxCachePoints === 'number') {
      params.maxCachePoints = Math.max(0, Math.floor(config.maxCachePoints))
    }
    if (typeof config.lodUpdateDistance === 'number') {
      params.lodUpdateDistance = Math.max(0, config.lodUpdateDistance)
    }
    if (typeof config.lodUpdateAngle === 'number') {
      params.lodUpdateAngle = Math.max(0, config.lodUpdateAngle)
    }
    if (typeof config.lodBehindPenalty === 'number') {
      params.lodBehindPenalty = Math.max(1, config.lodBehindPenalty)
    }
    if (typeof config.lodRangeMin === 'number') {
      params.lodRangeMin = Math.max(0, Math.floor(config.lodRangeMin))
    }
    if (typeof config.lodRangeMax === 'number') {
      params.lodRangeMax = Math.max(0, Math.floor(config.lodRangeMax))
    }
    if (typeof config.lodUnderfillLimit === 'number') {
      params.lodUnderfillLimit = Math.max(0, Math.floor(config.lodUnderfillLimit))
    }
    if (config.lodRefinementMode === 'step' || config.lodRefinementMode === 'direct') {
      params.lodRefinementMode = config.lodRefinementMode
    }
    if (typeof config.maxLoadsPerFrame === 'number') {
      params.maxLoadsPerFrame = Math.max(0, Math.floor(config.maxLoadsPerFrame))
    }
    if (typeof config.radialSorting === 'boolean') {
      params.radialSorting = config.radialSorting
    }
    if (typeof config.sortVisibilityCompact === 'boolean') {
      params.sortVisibilityCompact = config.sortVisibilityCompact
    }
    if (typeof config.sortVisibilityBehindMargin === 'number') {
      params.sortVisibilityBehindMargin = config.sortVisibilityBehindMargin
    }
    if (typeof config.sortVisibilityRotationRefreshAngle === 'number') {
      params.sortVisibilityRotationRefreshAngle = config.sortVisibilityRotationRefreshAngle
    }
    if (typeof config.sortVisibilityScreenCompact === 'boolean') {
      params.sortVisibilityScreenCompact = config.sortVisibilityScreenCompact
    }
    if (typeof config.sortVisibilityScreenMargin === 'number') {
      params.sortVisibilityScreenMargin = config.sortVisibilityScreenMargin
    }
    if (typeof config.worldStateUpdateInterval === 'number') {
      params.worldStateUpdateInterval = Math.max(0, Math.floor(config.worldStateUpdateInterval))
    }
  }

  private applyLodConfigToPlacement(placement: GSplatPlacement): void {
    const config = this.lodConfig
    if (typeof config.lodBaseDistance === 'number') {
      placement.lodBaseDistance = config.lodBaseDistance
    }
    if (typeof config.lodMultiplier === 'number') {
      placement.lodMultiplier = config.lodMultiplier
    }
  }

  private createManagerOptionsFromLodConfig(): GSplatManagerOptions {
    return {
      orderUploadScheduler: this.createOrderUploadSchedulerOptions(),
      interactiveSortScheduler: this.lodConfig.interactiveSortScheduler,
      onSortResultReady: () => this.emit('sortReady', {}),
    }
  }

  private createOrderUploadSchedulerOptions(): GSplatOrderUploadSchedulerOptions {
    const config = this.lodConfig
    const options: GSplatOrderUploadSchedulerOptions = {}

    if (config.orderUploadMode) options.orderUploadMode = config.orderUploadMode
    if (typeof config.minOrderUploadIntervalMs === 'number') {
      options.minOrderUploadIntervalMs = config.minOrderUploadIntervalMs
    }
    if (typeof config.maxOrderUploadDelayMs === 'number') {
      options.maxOrderUploadDelayMs = config.maxOrderUploadDelayMs
    }
    if (typeof config.maxImmediateOrderUploadBytes === 'number') {
      options.maxImmediateOrderUploadBytes = config.maxImmediateOrderUploadBytes
    }
    if (typeof config.maxOrderUploadBytesPerFrame === 'number') {
      options.maxOrderUploadBytesPerFrame = config.maxOrderUploadBytesPerFrame
    }
    if (typeof config.maxOrderUploadChunksPerFrame === 'number') {
      options.maxOrderUploadChunksPerFrame = config.maxOrderUploadChunksPerFrame
    }
    if (config.firstOrderUploadMode) {
      options.firstOrderUploadMode = config.firstOrderUploadMode
    }
    if (typeof config.allowFirstOrderFallback === 'boolean') {
      options.allowFirstOrderFallback = config.allowFirstOrderFallback
    }
    if (typeof config.allowOrderUploadFullFallback === 'boolean') {
      options.allowFullUploadFallback = config.allowOrderUploadFullFallback
    }

    return options
  }

  // ==================== 拾取 ====================

  /**
   * 按射线做 3DGS 拾取。
   *
   * @param ray - 世界空间射线
   * @param camera - 当前相机，用于 GPU 拾取视图
   * @param options - 可选拾取参数，控制 GPU/CPU 路径等
   * @returns 命中结果，未命中返回 null
   */
  pick(ray: THREE.Ray, camera: THREE.Camera, options?: PickOptions): PickResult | null {
    return this.operations.pick(ray, camera, this.frameUpdater.forceCpuPickNodeIds, options)
  }

  /**
   * 按屏幕坐标做 3DGS 拾取。
   *
   * @param screenX - 屏幕 x，单位 CSS 像素，原点左上
   * @param screenY - 屏幕 y，单位 CSS 像素，原点左上
   * @param camera - 当前相机
   * @param options - 可选拾取参数
   * @returns 命中结果，未命中返回 null
   */
  pickFromScreen(
    screenX: number,
    screenY: number,
    camera: THREE.Camera,
    options?: PickOptions
  ): PickResult | null {
    return this.operations.pickFromScreen(
      screenX,
      screenY,
      camera,
      this.frameUpdater.forceCpuPickNodeIds,
      options
    )
  }

  /** 释放 GPU 拾取器占用的显存资源。 */
  disposeGpuPicker(): void {
    this.operations.disposeGpuPicker()
  }

  /** 清除 GPU 拾取器缓存的 LOD 版本号，强制下次重采。 */
  clearGpuPickerLodVersions(): void {
    this.operations.clearGpuPickerLodVersions()
  }

  // ==================== 颜色空间管理 ====================

  /**
   * 设置模型的颜色调整参数。
   *
   * @param nodeId - 模型唯一标识
   * @param options - 亮度/色温/对比度/饱和度/透明度，未提供的字段保持原值
   * @returns 设置成功返回 true
   */
  setColorAdjustment(
    nodeId: string,
    options: {
      brightness?: number
      temperature?: number
      contrast?: number
      saturation?: number
      opacity?: number
    }
  ): boolean {
    return this.operations.setColorAdjustment(nodeId, options)
  }

  /**
   * 获取模型当前的颜色调整参数。
   *
   * @param nodeId - 模型唯一标识
   * @returns 当前参数快照，未找到返回 undefined
   */
  getColorAdjustment(nodeId: string):
    | {
        brightness: number
        temperature: number
        contrast: number
        saturation: number
        opacity: number
      }
    | undefined {
    return this.operations.getColorAdjustment(nodeId)
  }

  /**
   * 重置模型的颜色调整参数到默认值。
   *
   * @param nodeId - 模型唯一标识
   * @returns 重置成功返回 true
   */
  resetColorAdjustment(nodeId: string): boolean {
    return this.operations.resetColorAdjustment(nodeId)
  }

  // ==================== 标量过滤 ====================

  /**
   * 按标量属性过滤显示范围。
   *
   * @param nodeId - 模型唯一标识
   * @param options - 过滤配置，含属性名与阈值区间
   * @returns 设置成功返回 true
   */
  setScalarFilter(nodeId: string, options: ScalarFilterOptions): boolean {
    return this.operations.setScalarFilter(nodeId, options)
  }

  /**
   * 清除标量过滤。
   *
   * @param nodeId - 模型唯一标识
   * @returns 清除成功返回 true
   */
  clearScalarFilter(nodeId: string): boolean {
    return this.operations.clearScalarFilter(nodeId)
  }

  setShaderEffect(nodeId: string, effect: GSplatShaderEffectLike | null): boolean {
    return this.operations.setShaderEffect(nodeId, effect)
  }

  setClipVolumes(volumes: ReadonlyArray<ClipVolume>): boolean {
    const nextClipState = createGSplatClipState(volumes)
    if (areGSplatClipStatesEqual(this.clipState, nextClipState)) {
      return false
    }

    this.clipState = nextClipState
    for (const entry of this.registry.lodGroups.values()) {
      entry.renderMesh.setClipState(this.clipState)
    }
    this.operations.clearGpuPickerLodVersions()
    return true
  }

  clearShaderEffect(nodeId: string): boolean {
    return this.operations.clearShaderEffect(nodeId)
  }

  restartShaderEffect(nodeId: string): boolean {
    return this.operations.restartShaderEffect(nodeId)
  }

  setShaderEffectUniform(nodeId: string, name: string, value: unknown): boolean {
    return this.operations.setShaderEffectUniform(nodeId, name, value)
  }

  setWorkBufferModifier(nodeId: string, modifier: GSplatWorkBufferModifier | null): boolean {
    return this.operations.setWorkBufferModifier(nodeId, modifier)
  }

  setWorkBufferUpdateMode(nodeId: string, mode: GSplatWorkBufferUpdateMode): boolean {
    return this.operations.setWorkBufferUpdateMode(nodeId, mode)
  }

  /**
   * 计算指定标量属性的直方图。
   *
   * @param nodeId - 模型唯一标识
   * @param options - 直方图配置，含属性名、分箱数等
   * @returns 直方图结果；模型不存在返回 null
   */
  async getScalarHistogram(
    nodeId: string,
    options: ScalarHistogramOptions
  ): Promise<ScalarHistogramResult | null> {
    return this.operations.getScalarHistogram(nodeId, options)
  }

  // ==================== 颜色空间 ====================

  /**
   * 合并更新全局颜色空间配置。
   *
   * @param config - 局部配置，仅覆盖提供的字段
   */
  setColorSpaceConfig(config: Partial<ColorSpaceConfig>): void {
    this.operations.setColorSpaceConfig(config)
  }

  /**
   * 获取全局颜色空间配置。
   *
   * @returns 配置副本，未设置时返回 undefined
   */
  getColorSpaceConfig(): ColorSpaceConfig | undefined {
    return this.operations.getColorSpaceConfig()
  }

  // ==================== 选中 ====================

  /**
   * 标记指定 splat 为选中或取消选中。
   *
   * @param nodeId - 模型唯一标识
   * @param splatIndices - splat 索引数组
   * @param selected - true 选中，false 取消
   * @returns 操作成功返回 true
   */
  setSelected(nodeId: string, splatIndices: number[], selected: boolean): boolean {
    return this.operations.setSelected(nodeId, splatIndices, selected)
  }

  /**
   * 清除当前模型的全部选中。
   *
   * @param nodeId - 模型唯一标识
   * @returns 操作成功返回 true
   */
  clearSelection(nodeId: string): boolean {
    return this.operations.clearSelection(nodeId)
  }

  // ==================== 中心点拾取 ====================

  /**
   * 按点位取 splat 中心点。
   *
   * @param screenX - 屏幕 x，单位 CSS 像素，原点左上
   * @param screenY - 屏幕 y，单位 CSS 像素，原点左上
   * @param camera - 当前相机
   * @param options - 可选拾取参数
   * @returns 命中中心点数组，按距离近到远
   */
  pickCenter(
    screenX: number,
    screenY: number,
    camera: THREE.Camera,
    options?: CentersPickOptions
  ): CentersPickResult[] {
    return this.operations.pickCenter(screenX, screenY, camera, options)
  }

  /**
   * 按屏幕矩形框选 splat 中心点。
   *
   * @param startX - 起点 x，CSS 像素
   * @param startY - 起点 y，CSS 像素
   * @param endX - 终点 x，CSS 像素
   * @param endY - 终点 y，CSS 像素
   * @param camera - 当前相机
   * @param maxResults - 返回上限，默认 10000
   * @returns 命中中心点数组
   */
  pickCentersInRect(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    camera: THREE.Camera,
    maxResults = 10000
  ): CentersPickResult[] {
    return this.operations.pickCentersInRect(startX, startY, endX, endY, camera, maxResults)
  }

  // ==================== 中心点渲染模式 ====================

  /**
   * 设置中心点显示模式。
   *
   * @param nodeId - 模型唯一标识
   * @param mode - 显示模式枚举
   * @returns 设置成功返回 true
   */
  setCentersDisplayMode(nodeId: string, mode: CentersDisplayMode): boolean {
    return this.operations.setCentersDisplayMode(nodeId, mode)
  }

  /**
   * 获取当前的中心点显示模式。
   *
   * @param nodeId - 模型唯一标识
   * @returns 当前模式，未找到返回 undefined
   */
  getCentersDisplayMode(nodeId: string): CentersDisplayMode | undefined {
    return this.operations.getCentersDisplayMode(nodeId)
  }

  /**
   * 合并更新中心点样式。
   *
   * @param nodeId - 模型唯一标识
   * @param style - 局部样式，仅覆盖提供的字段
   * @returns 设置成功返回 true
   */
  setCentersStyle(nodeId: string, style: Partial<CentersStyle>): boolean {
    return this.operations.setCentersStyle(nodeId, style)
  }

  /**
   * 获取当前中心点样式。
   *
   * @param nodeId - 模型唯一标识
   * @returns 样式副本，未找到返回 undefined
   */
  getCentersStyle(nodeId: string): CentersStyle | undefined {
    return this.operations.getCentersStyle(nodeId)
  }
}

export default PlayCanvasGS3DManager
